import React, { useState } from 'react';
import './Projects.css';

const Projects = () => {
  // Project Data
  // Project Data
  const projectsData = [
    {
      id: 'nmap_simulator',
      name: 'Nmap_Simulator',
      filename: 'network_scanner.jsx',
      subtitle: 'Interactive Network Scanning Visualizer',
      status: 'stable',
      version: 'v1.0 STABLE',
      description: '/* Objective: Educational network scanning simulator that visualizes TCP/UDP packet flows in real-time. Features include animated packet visualization with color-coded flags (SYN, ACK, RST), firewall simulation, manual/auto modes, and support for multiple scan types including SYN scan, Connect scan, UDP scan, and Ping scan with realistic network topology display. */',
      techStack: [
        { name: 'React + Hooks', percent: 100 },
        { name: 'Framer Motion', percent: 90 },
        { name: 'Network Simulation', percent: 85 },
        { name: 'Real-time Animation', percent: 95 }
      ],
      terminalOutput: (
        <>
          <span className="term-green">$ nmap -sS -T4 192.168.1.1</span>{'\n'}
          {'\n'}
          <span className="term-blue">[*] Starting TCP SYN Scan...</span>{'\n'}
          <span className="term-blue">[*] Timing Template: Aggressive (T4)</span>{'\n'}
          {'\n'}
          <span className="term-yellow">[+] 192.168.1.1:80 - SYN → SYN-ACK</span>{'\n'}
          <span className="term-green">[✓] Port 80/tcp OPEN (http)</span>{'\n'}
          <span className="term-yellow">[+] 192.168.1.1:443 - SYN → RST-ACK</span>{'\n'}
          <span className="term-red">[✗] Port 443/tcp CLOSED</span>{'\n'}
          <span className="term-yellow">[+] 192.168.1.1:22 - SYN → [FILTERED]</span>{'\n'}
          {'\n'}
          <span className="term-green">[+] Scan Complete. 1 open port found.</span>{'\n'}
          <span className="term-blue">[INFO] Interactive visualization available</span>
        </>
      ),
      links: {
        source: 'https://github.com/DiptanshuDhawan',
        demo: 'https://nmapsim.diptanshu.tech' 
      }
    },
    {
      id: 'permission_scanner',
      name: 'Permission_Scanner',
      filename: 'permission_audit.sh',
      subtitle: 'Linux File Permission Security Auditor',
      status: 'stable', 
      version: 'v1.0 STABLE',
      description: '/* Objective: Scan, detect, and fix insecure file and directory permissions on Linux systems. Provides dry-run safety mode, detailed logging, custom permission fixes, and disk usage analysis to prevent privilege escalation vulnerabilities. */',
      techStack: [
        { name: 'Bash Scripting', percent: 100 },
        { name: 'Linux File Systems', percent: 85 },
        { name: 'Security Auditing', percent: 75 }
      ],
      terminalOutput: (
        <>
          <span className="term-green">$ sudo ./permission_audit.sh --target-dir /var/www --verbose</span>{'\n'}
          {'\n'}
          <span className="term-blue">[*] Starting Permission Audit...</span>{'\n'}
          <span className="term-blue">[*] Target Directory: /var/www</span>{'\n'}
          <span className="term-blue">[*] Mode: DRY-RUN (Safe Preview)</span>{'\n'}
          {'\n'}
          <span className="term-yellow">[!] RISK DETECTED: /var/www/config.php (777)</span>{'\n'}
          <span className="term-yellow">[!] RISK DETECTED: /var/www/uploads/ (777)</span>{'\n'}
          <span className="term-red">[CRITICAL] World-writable files found: 2</span>{'\n'}
          {'\n'}
          <span className="term-green">[+] Scan Complete. Log saved to /var/log/permission_audit/</span>{'\n'}
          <span className="term-blue">[INFO] Run with --fix to apply secure permissions</span>
        </>
      ),
      links: {
        source: 'https://github.com/DiptanshuDhawan/permission-scanner',
        demo: 'https://youtu.be/RW35vCuk3Vs'
      }
    }
  ];

  const [selectedId, setSelectedId] = useState('nmap_simulator');

  const activeProject = projectsData.find(p => p.id === selectedId) || projectsData[0];

  return (
    <div className="dossier-container">
      {/* LEFT COLUMN: PROJECT EXPLORER */}
      <div className="project-explorer-column">
        <div className="explorer-header">AVAILABLE MODULES</div>
        <div className="treenode-list">
          {projectsData.map((project) => (
            <div 
              key={project.id}
              className={`treenode-item ${selectedId === project.id ? 'active' : ''}`}
              onClick={() => setSelectedId(project.id)}
            >
              <span className={`status-dot status-dot-${project.status}`}></span>
              {project.name}
            </div>
          ))}
        </div>
      </div>

      {/* RIGHT COLUMN: READ VIEW */}
      <div className="readme-column">
        
        {/* Header Section */}
        <div className="project-header">
          <div className="project-title-row">
            <div className="project-title">{activeProject.filename}</div>
            <div className={`status-badge status-${activeProject.status}`}>
              [{activeProject.version}]
            </div>
          </div>
          <div className="project-subtitle">{activeProject.subtitle}</div>
        </div>

        {/* Description Section */}
        <div className="readme-section">
          <div className="section-label">
            <span className="title-icon">🚩</span> MISSION OBJECTIVE
          </div>
          <div className="description-block">
            {activeProject.description}
          </div>
        </div>

        {/* Tech Stack Section */}
        <div className="readme-section">
          <div className="section-label">
            <span className="title-icon">⚡</span> TECH STACK COMPOSITION
          </div>
          {activeProject.techStack.map((tech, idx) => (
            <div key={idx} className="project-skill-bar">
              <div className="project-skill-header">
                <span className="project-skill-name">{tech.name}</span>
                <span className="project-skill-percent">{tech.percent}%</span>
              </div>
              <div className="project-skill-track">
                <div 
                  className="project-skill-fill" 
                  style={{ width: `${tech.percent}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Terminal Preview Section */}
        <div className="readme-section">
          <div className="section-label">
            <span className="title-icon">📺</span> TERMINAL OUTPUT
          </div>
          <div className="terminal-preview-box">
            {activeProject.terminalOutput}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons-row">
          <a href={activeProject.links.source} target="_blank" rel="noopener noreferrer" className="action-btn btn-primary">
             <span>GITHUB REPO</span>
          </a>
          <a href={activeProject.links.demo} target="_blank" rel="noopener noreferrer" className="action-btn btn-secondary">
             <span>LIVE DEMO</span>
          </a>
        </div>

      </div>
    </div>
  );
};

export default Projects;
